import { AuthService } from '../services/AuthService';
import { AuthorizationService, Permission } from '../services/AuthorizationService';
import { TokenService, TokenStatus } from '../services/TokenService';
import { AuthRepositoryFactory } from '../repositories/AuthRepositoryFactory';
import { AuthLocalStorageAdapter } from '../repositories/AuthLocalStorageAdapter';
import { User } from '../models/User';
import { AuthToken } from '../value-objects/AuthToken';

/**
 * 권한 확인 요청 DTO
 */
export interface CheckAuthRequest {
  resource?: string;
  action?: string;
  context?: Record<string, any>;
  refreshIfNeeded?: boolean;
}

/**
 * 인증 상태 응답 DTO
 */
export interface AuthStatusResponse {
  isAuthenticated: boolean;
  user?: {
    id: string;
    email: string;
    name: string;
    role: string;
    isActive: boolean;
    organization_id?: string;
    organization_name?: string;
    profileImageUrl?: string;
    lastLoginAt?: Date;
    accountLocked?: boolean;
    passwordChangeRequired?: boolean;
  };
  token?: {
    isValid: boolean;
    expiresAt: Date;
    expiresIn: number; // seconds
    needsRefresh: boolean;
    status: TokenStatus;
  };
  session?: {
    id: string;
    isValid: boolean;
    expiresAt?: Date;
    lastAccessedAt?: Date;
    inactiveMinutes: number;
  };
  permissions?: {
    hasPermission: boolean;
    reason?: string;
    allowedActions?: string[];
  };
  security?: {
    requiresAction?: string[];
    recommendations?: string[];
    riskLevel?: 'low' | 'medium' | 'high';
  };
  error?: string;
  errorCode?: string;
}

/**
 * 권한 확인 응답 DTO
 */
export interface PermissionCheckResponse {
  hasPermission: boolean;
  reason?: string;
  user?: AuthStatusResponse['user'];
  context?: Record<string, any>;
  error?: string;
  errorCode?: string;
}

/**
 * 인증 상태 확인 Use Case
 * 사용자의 인증 상태와 권한을 확인하는 애플리케이션 서비스
 */
export class CheckAuthUseCase {
  private authService: AuthService;
  private tokenService: TokenService;

  constructor() {
    const repository = AuthRepositoryFactory.getInstance();
    this.authService = new AuthService(repository);
    this.tokenService = new TokenService(repository);
  }

  /**
   * 전체 인증 상태 확인
   */
  async execute(request: CheckAuthRequest = {}): Promise<AuthStatusResponse> {
    try {
      // 1. 기본 인증 상태 확인
      const isAuthenticated = this.authService.isAuthenticated();
      
      if (!isAuthenticated) {
        return {
          isAuthenticated: false,
          error: '인증되지 않은 사용자입니다.',
          errorCode: 'NOT_AUTHENTICATED',
        };
      }

      // 2. 세션 유효성 검사 및 자동 갱신
      const sessionValidation = await this.validateAndRefreshSession(request.refreshIfNeeded);
      
      if (!sessionValidation.isValid) {
        return {
          isAuthenticated: false,
          error: sessionValidation.error,
          errorCode: sessionValidation.errorCode,
        };
      }

      // 3. 사용자 정보 조회
      const user = await this.authService.getCurrentUser();
      if (!user) {
        return {
          isAuthenticated: false,
          error: '사용자 정보를 찾을 수 없습니다.',
          errorCode: 'USER_NOT_FOUND',
        };
      }

      // 4. 토큰 상태 확인
      const tokenStatus = this.getTokenStatus();

      // 5. 세션 정보 확인
      const sessionXCircle = this.getSessionXCircle();

      // 6. 권한 확인 (요청된 경우)
      let permissionCheck: AuthStatusResponse['permissions'] | undefined;
      if (request.resource && request.action) {
        const permission: Permission = {
          resource: request.resource,
          action: request.action,
          context: request.context,
        };
        
        const authResult = AuthorizationService.hasPermission(user, permission);
        permissionCheck = {
          hasPermission: authResult.allowed,
          reason: authResult.reason,
          allowedActions: this.getAllowedActions(user, request.resource),
        };
      }

      // 7. 보안 권장사항 확인
      const securityXCircle = this.getSecurityXCircle(user);

      return {
        isAuthenticated: true,
        user: this.mapUserToResponse(user),
        token: tokenStatus,
        session: sessionXCircle,
        permissions: permissionCheck,
        security: securityXCircle,
      };

    } catch (error) {
      console.error('인증 상태 확인 중 오류:', error);
      return {
        isAuthenticated: false,
        error: '인증 상태 확인 중 오류가 발생했습니다.',
        errorCode: 'AUTH_CHECK_ERROR',
      };
    }
  }

  /**
   * 단순 권한 확인
   */
  async checkPermission(request: {
    resource: string;
    action: string;
    context?: Record<string, any>;
  }): Promise<PermissionCheckResponse> {
    try {
      // 1. 인증 상태 확인
      const isAuthenticated = this.authService.isAuthenticated();
      if (!isAuthenticated) {
        return {
          hasPermission: false,
          error: '인증되지 않은 사용자입니다.',
          errorCode: 'NOT_AUTHENTICATED',
        };
      }

      // 2. 사용자 정보 조회
      const user = await this.authService.getCurrentUser();
      if (!user) {
        return {
          hasPermission: false,
          error: '사용자 정보를 찾을 수 없습니다.',
          errorCode: 'USER_NOT_FOUND',
        };
      }

      // 3. 권한 확인
      const permission: Permission = {
        resource: request.resource,
        action: request.action,
        context: request.context,
      };

      const authResult = AuthorizationService.hasPermission(user, permission);

      return {
        hasPermission: authResult.allowed,
        reason: authResult.reason,
        user: this.mapUserToResponse(user),
        context: request.context,
      };

    } catch (error) {
      console.error('권한 확인 중 오류:', error);
      return {
        hasPermission: false,
        error: '권한 확인 중 오류가 발생했습니다.',
        errorCode: 'PERMISSION_CHECK_ERROR',
      };
    }
  }

  /**
   * 다중 권한 확인
   */
  async checkMultiplePermissions(
    permissions: Array<{
      resource: string;
      action: string;
      context?: Record<string, any>;
    }>
  ): Promise<{
    user?: AuthStatusResponse['user'];
    results: Array<{
      resource: string;
      action: string;
      hasPermission: boolean;
      reason?: string;
    }>;
    error?: string;
    errorCode?: string;
  }> {
    try {
      // 1. 사용자 정보 조회
      const user = await this.authService.getCurrentUser();
      if (!user) {
        return {
          results: [],
          error: '사용자 정보를 찾을 수 없습니다.',
          errorCode: 'USER_NOT_FOUND',
        };
      }

      // 2. 각 권한 확인
      const results = permissions.map(({ resource, action, context }) => {
        const permission: Permission = { resource, action, context };
        const authResult = AuthorizationService.hasPermission(user, permission);
        
        return {
          resource,
          action,
          hasPermission: authResult.allowed,
          reason: authResult.reason,
        };
      });

      return {
        user: this.mapUserToResponse(user),
        results,
      };

    } catch (error) {
      console.error('다중 권한 확인 중 오류:', error);
      return {
        results: [],
        error: '권한 확인 중 오류가 발생했습니다.',
        errorCode: 'MULTI_PERMISSION_CHECK_ERROR',
      };
    }
  }

  /**
   * 역할 기반 접근 확인
   */
  async checkRoleAccess(requiredRole: string): Promise<PermissionCheckResponse> {
    try {
      const user = await this.authService.getCurrentUser();
      if (!user) {
        return {
          hasPermission: false,
          error: '사용자 정보를 찾을 수 없습니다.',
          errorCode: 'USER_NOT_FOUND',
        };
      }

      const hasRole = AuthorizationService.hasRole(user, requiredRole);

      return {
        hasPermission: hasRole,
        reason: hasRole ? undefined : `${requiredRole} 역할이 필요합니다.`,
        user: this.mapUserToResponse(user),
      };

    } catch (error) {
      console.error('역할 확인 중 오류:', error);
      return {
        hasPermission: false,
        error: '역할 확인 중 오류가 발생했습니다.',
        errorCode: 'ROLE_CHECK_ERROR',
      };
    }
  }

  /**
   * 세션 유효성 검사 및 자동 갱신
   */
  private async validateAndRefreshSession(refreshIfNeeded = true): Promise<{
    isValid: boolean;
    error?: string;
    errorCode?: string;
  }> {
    try {
      const validation = await this.authService.validateAndRefreshSession();
      
      if (validation.needsLogin) {
        return {
          isValid: false,
          error: '로그인이 필요합니다.',
          errorCode: 'LOGIN_REQUIRED',
        };
      }

      if (!validation.isValid) {
        return {
          isValid: false,
          error: '세션이 유효하지 않습니다.',
          errorCode: 'INVALID_SESSION',
        };
      }

      // 토큰 갱신이 필요한 경우 자동 갱신
      if (refreshIfNeeded) {
        const tokenValid = await this.tokenService.autoRefreshIfNeeded();
        if (!tokenValid) {
          return {
            isValid: false,
            error: '토큰 갱신에 실패했습니다.',
            errorCode: 'TOKEN_REFRESH_FAILED',
          };
        }
      }

      return { isValid: true };

    } catch (error) {
      console.error('세션 검증 중 오류:', error);
      return {
        isValid: false,
        error: '세션 검증 중 오류가 발생했습니다.',
        errorCode: 'SESSION_VALIDATION_ERROR',
      };
    }
  }

  /**
   * 토큰 상태 정보 조회
   */
  private getTokenStatus(): AuthStatusResponse['token'] {
    const validation = this.tokenService.validateToken();
    const timeLeft = this.tokenService.getTimeUntilExpiry();
    const expiresIn = Math.floor(timeLeft / 1000);

    return {
      isValid: validation.status === TokenStatus.VALID,
      expiresAt: validation.token?.expiresAt || new Date(),
      expiresIn: Math.max(0, expiresIn),
      needsRefresh: validation.needsRefresh || false,
      status: validation.status,
    };
  }

  /**
   * 세션 정보 조회
   */
  private getSessionXCircle(): AuthStatusResponse['session'] | undefined {
    const sessionData = AuthLocalStorageAdapter.loadSession();
    if (!sessionData) {
      return undefined;
    }

    const validation = AuthLocalStorageAdapter.validateSession();
    const inactiveMinutes = AuthLocalStorageAdapter.getInactiveMinutes();

    return {
      id: sessionData.id,
      isValid: validation.isValid,
      expiresAt: sessionData.expiresAt ? new Date(sessionData.expiresAt) : undefined,
      lastAccessedAt: sessionData.lastAccessedAt ? new Date(sessionData.lastAccessedAt) : undefined,
      inactiveMinutes,
    };
  }

  /**
   * 보안 정보 조회
   */
  private getSecurityXCircle(user: User): AuthStatusResponse['security'] {
    const requiresAction: string[] = [];
    const recommendations: string[] = [];
    let riskLevel: 'low' | 'medium' | 'high' = 'low';

    // 계정 잠금 확인
    if (user.isLocked()) {
      requiresAction.push('account_unlock');
      riskLevel = 'high';
    }

    // 비밀번호 변경 필요 확인
    if (user.isPasswordChangeRequired()) {
      requiresAction.push('password_change');
      riskLevel = riskLevel === 'high' ? 'high' : 'medium';
    }

    // 비밀번호 변경 권장
    if (user.passwordChangedAt) {
      const daysSinceChange = Math.floor(
        (Date.now() - user.passwordChangedAt.getTime()) / (1000 * 60 * 60 * 24)
      );
      
      if (daysSinceChange > 90) {
        recommendations.push('비밀번호를 90일 이상 사용하고 있습니다. 보안을 위해 변경을 권장합니다.');
        riskLevel = riskLevel === 'low' ? 'medium' : riskLevel;
      }
    }

    // 비활성 시간 확인
    const inactiveMinutes = AuthLocalStorageAdapter.getInactiveMinutes();
    const securitySettings = AuthLocalStorageAdapter.loadSecuritySettings();
    
    if (inactiveMinutes > securitySettings.autoLogoutMinutes * 0.8) {
      recommendations.push('장시간 비활성 상태입니다. 자동 로그아웃이 곧 실행될 수 있습니다.');
    }

    return {
      requiresAction: requiresAction.length > 0 ? requiresAction : undefined,
      recommendations: recommendations.length > 0 ? recommendations : undefined,
      riskLevel,
    };
  }

  /**
   * 허용된 액션 목록 조회
   */
  private getAllowedActions(user: User, resource: string): string[] {
    // 간단한 예시 구현
    // 실제로는 더 복잡한 권한 매트릭스 사용
    const roleActions: { [key: string]: string[] } = {
      'admin': ['read', 'create', 'update', 'delete', 'manage'],
      'owner': ['read', 'create', 'update', 'delete'],
      'student': ['read', 'update'],
      'parent': ['read'],
      'guest': ['read'],
    };

    return roleActions[user.role.value] || [];
  }

  /**
   * User 엔티티를 응답 DTO로 변환
   */
  private mapUserToResponse(user: User): AuthStatusResponse['user'] {
    return {
      id: user.id.value,
      email: user.email.value,
      name: user.name,
      role: user.role.value,
      isActive: user.isActive,
      organization_id: user.organizationId,
      organization_name: user.organizationName,
      profileImageUrl: user.profileImageUrl,
      lastLoginAt: user.lastLoginAt,
      accountLocked: user.isLocked(),
      passwordChangeRequired: user.isPasswordChangeRequired(),
    };
  }

  /**
   * 현재 사용자 정보 간단 조회
   */
  async getCurrentUser(): Promise<AuthStatusResponse['user'] | null> {
    try {
      const user = await this.authService.getCurrentUser();
      return user ? this.mapUserToResponse(user) : null;
    } catch (error) {
      console.error('현재 사용자 조회 중 오류:', error);
      return null;
    }
  }

  /**
   * 토큰 수동 갱신
   */
  async refreshToken(): Promise<{
    success: boolean;
    token?: AuthStatusResponse['token'];
    error?: string;
  }> {
    try {
      const result = await this.tokenService.refreshToken();
      
      if (!result.success) {
        return {
          success: false,
          error: result.error,
        };
      }

      return {
        success: true,
        token: this.getTokenStatus(),
      };

    } catch (error) {
      console.error('토큰 갱신 중 오류:', error);
      return {
        success: false,
        error: '토큰 갱신 중 오류가 발생했습니다.',
      };
    }
  }
}